import { cn } from "../lib/cn";

/** How hard the tag is written. Ink weight only - a tag never carries status color. */
export type TagTone = "neutral" | "emphasis";

const toneClasses: Record<TagTone, string> = {
  neutral: "border-rule text-annotation",
  emphasis: "border-ink text-ink",
};

export interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** `neutral` is every ordinary fact; `emphasis` is the one that carries weight
      (a leadership role among ordinary ones). Defaults to `neutral`. */
  tone?: TagTone;
  /** A small glyph before the word, saying what kind of fact this is (a person
      vs a group). Decorative: the word is the label, the icon only sorts it. */
  icon?: React.ReactNode;
  /** The fact itself. One or two words: a role, a kind, a scope. */
  children: React.ReactNode;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * A paper tag tied to a thing, naming what it is: a role ("Practice Manager"),
 * a kind, a scope. Hairline border, its own card stock, mono small-caps.
 *
 * A tag states a **fact**, not a state. Facts do not change while you watch
 * them, so a tag is never a highlighter: no `bg-wash-*`, no status ink, no
 * color coding. Emphasis, where a tag earns it, is ink weight.
 *
 * - `Tag` - what something *is*. A role, a category, a scope. Neutral ink.
 * - `Swipe` - what something is *doing right now*. Working, flagged, stopped.
 *   A word under a marker stroke.
 * - `LabelTape` - the *name* of a physical object in the clinic. A room, a
 *   machine. Someone walked over and stuck it on.
 *
 * If the word names a state, it wants a Swipe. If it names a piece of hardware,
 * it wants tape. Everything else that needs saying in two words is a tag.
 *
 * Sized off its own type (`leading-none`), so a tag dropped on ruled paper fits
 * inside one rule instead of pushing the ruling apart, and its fill knocks the
 * rule out behind it. Long text truncates rather than widening the line.
 */
export function Tag({
  tone = "neutral",
  icon,
  className,
  children,
  ref,
  ...rest
}: TagProps) {
  return (
    <span
      ref={ref}
      className={cn(
        "inline-flex min-w-0 max-w-full items-center gap-1 align-middle",
        "rounded-full border bg-card px-1.5 py-0.5 leading-none",
        "text-annotation-caps",
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      {icon && (
        <span aria-hidden="true" className="grid shrink-0 place-items-center">
          {icon}
        </span>
      )}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}
